import {getTestProps} from '@benchling/js-test-utils';
import {Button, Group, Kbd, Text, createStyles} from '@mantine/core';
import React from 'react';

import * as benchlingUtil from 'BenchlingUtil';
import {openOmnibox, useOmniboxAvailable} from 'omnibox/OmniboxOpenControl';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const SEARCH_BUTTON_WIDTH = 225;
const shortcutText = benchlingUtil.isMac() ? '⌘J' : 'Ctrl+J';

const useStyles = createStyles((theme) => ({
  root: {
    width: SEARCH_BUTTON_WIDTH,
    padding: `0 ${theme.spacing.sm}`,
    color: theme.colors.gray[6],
    backgroundColor: theme.colors.gray[0],
    borderColor: theme.colors.gray[3],
    fontWeight: 400,
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
    },
  },
  inner: {
    width: '100%',
  },
  label: {
    width: '100%',
  },
  searchIcon: {
    width: 12,
    height: 12,
  },
  shortcut: {
    padding: 0,
    color: theme.colors.gray[6],
    backgroundColor: 'transparent',
    border: 0,
    boxShadow: 'none',
    fontFamily: theme.fontFamily,
    fontSize: theme.fontSizes.xs,
    fontWeight: 400,
    lineHeight: 1,
  },
}));

/** Opens the omnibox from the center of the homepage header when that trigger is available. */
export default function HomepageSearchButton(): JSX.Element | null {
  const omniboxAvailable = useOmniboxAvailable();
  const {classes} = useStyles();

  if (!omniboxAvailable) {
    return null;
  }

  return (
    <Button
      aria-haspopup="dialog"
      aria-label="Search Benchling"
      classNames={{root: classes.root, inner: classes.inner, label: classes.label}}
      onClick={openOmnibox}
      radius="md"
      size="md"
      variant="secondary"
      {...getTestProps({component: 'HomepageSearchButton', element: 'root'})}
    >
      <Group position="apart" spacing="xs" noWrap={true} w="100%">
        <Group spacing="xs" noWrap={true}>
          <BenchlingIcon aria-hidden={true} icon="search" className={classes.searchIcon} />
          <Text color="dimmed" size="sm">
            Search Benchling
          </Text>
        </Group>
        <Kbd className={classes.shortcut}>{shortcutText}</Kbd>
      </Group>
    </Button>
  );
}
